import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, FormField } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { entitiesApi, personsApi } from "@/api/counterparties";
import { useCreateInvestor } from "@/hooks/capitalMarkets/useInvestors";
import {
  ACCREDITATION_BASES,
  INVESTOR_STATUSES,
  type AccreditationBasis,
  type InvestorStatus,
  type InvestorType,
} from "@/types/investors";
import { humanizeStatus } from "../shared/format";

type IdentityKind = "individual" | "institutional";

const INSTITUTIONAL_TYPES: InvestorType[] = [
  "Institutional",
  "FamilyOffice",
  "Fund",
  "QIB",
];

export function NewInvestorModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const create = useCreateInvestor();

  const [kind, setKind] = useState<IdentityKind>("individual");
  const [counterpartyId, setCounterpartyId] = useState("");
  const [investorType, setInvestorType] = useState<InvestorType>(
    "IndividualAccredited",
  );
  const [basis, setBasis] = useState<AccreditationBasis>("IncomeTest");
  const [status, setStatus] = useState<InvestorStatus>("Prospect");

  const personsQuery = useQuery({
    queryKey: ["counterparties", "persons", "picker"],
    queryFn: () => personsApi.list({ pageSize: 200 }),
    enabled: kind === "individual",
  });
  const entitiesQuery = useQuery({
    queryKey: ["counterparties", "entities", "picker"],
    queryFn: () => entitiesApi.list({ pageSize: 200 }),
    enabled: kind === "institutional",
  });

  const options = useMemo(() => {
    if (kind === "individual") {
      return (personsQuery.data?.data ?? []).map((p) => ({
        id: p.id,
        label: p.displayName,
      }));
    }
    return (entitiesQuery.data?.data ?? []).map((e) => ({
      id: e.id,
      label: e.legalEntityName,
    }));
  }, [kind, personsQuery.data, entitiesQuery.data]);

  const switchKind = (next: IdentityKind) => {
    setKind(next);
    setCounterpartyId("");
    setInvestorType(next === "individual" ? "IndividualAccredited" : "Institutional");
  };

  const handleSave = () => {
    if (!counterpartyId) {
      toast.error("Select a counterparty record first.");
      return;
    }
    create
      .mutateAsync({
        personId: kind === "individual" ? counterpartyId : null,
        entityId: kind === "institutional" ? counterpartyId : null,
        investorType,
        accreditationBasis: basis,
        preferredCommunicationMethod: "Email",
        status,
      })
      .then((investor) => {
        toast.success("Investor created.");
        onCreated(investor.id);
      })
      .catch((err) => toast.error(extractErrorMessage(err)));
  };

  return (
    <Modal
      open
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="New Investor"
      description="Create a reusable investor master record linked to a Person or Entity."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={create.isPending} onClick={handleSave}>
            Create Investor
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-3">
        <fieldset className="flex flex-col gap-space-2">
          <legend className="text-label font-display uppercase tracking-label text-gray-mid mb-space-1">
            Investor identity
          </legend>
          <label className="flex items-center gap-space-2 text-small">
            <input
              type="radio"
              name="identity-kind"
              checked={kind === "individual"}
              onChange={() => switchKind("individual")}
            />
            Individual accredited investor
          </label>
          <label className="flex items-center gap-space-2 text-small">
            <input
              type="radio"
              name="identity-kind"
              checked={kind === "institutional"}
              onChange={() => switchKind("institutional")}
            />
            Institutional / fund / family office
          </label>
        </fieldset>

        <FormField
          label={kind === "individual" ? "Linked Person" : "Linked Entity"}
          required
        >
          <Select
            value={counterpartyId}
            onChange={(e) => setCounterpartyId(e.target.value)}
            aria-label="Counterparty record"
          >
            <option value="">Select a record…</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>

        {kind === "institutional" && (
          <FormField label="Investor type">
            <Select
              value={investorType}
              onChange={(e) => setInvestorType(e.target.value as InvestorType)}
            >
              {INSTITUTIONAL_TYPES.map((t) => (
                <option key={t} value={t}>
                  {humanizeStatus(t)}
                </option>
              ))}
            </Select>
          </FormField>
        )}

        <FormField label="Accreditation basis">
          <Select
            value={basis}
            onChange={(e) => setBasis(e.target.value as AccreditationBasis)}
          >
            {ACCREDITATION_BASES.map((b) => (
              <option key={b} value={b}>
                {humanizeStatus(b)}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField label="Status">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as InvestorStatus)}
          >
            {INVESTOR_STATUSES.map((s) => (
              <option key={s} value={s}>
                {humanizeStatus(s)}
              </option>
            ))}
          </Select>
        </FormField>
      </div>
    </Modal>
  );
}
